import React, { useCallback, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useToast, Button } from '@app/core';
import { ErrorMsg } from '@app/form';
import { useAddPaymentMethod } from 'queries';
import { PaymentMethodContainer } from './PaymentMethodContainer';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@app/form';

const useStyles = createUseStyles((theme: Theme) => ({
  stripeContainer: {
    flex: 1,
  },
  errorMsg: {
    marginTop: theme.marginBase * 2,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  checkboxes: {
    marginBottom: theme.marginBase,
  },
}));

interface Props {
  redirectTo: string;
  organizationId: string;
  onSuccess?: (pm: string) => any;
  onCancel?: () => any;
  intentSecret?: string;
  selectableStyle?: boolean;
  sessionId?: string;
}

export const PaymentMethodForm = ({
  redirectTo,
  organizationId,
  onSuccess,
  onCancel,
  intentSecret,
  selectableStyle,
  sessionId,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();

  const { mutateAsync: addPaymentMethod } = useAddPaymentMethod(organizationId);

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isSubmitting, setSubmitting] = useState(false);
  const [reuse, setReuse] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const toggleReuse = useCallback(
    () => setReuse((reuse) => !reuse),
    [setReuse],
  );
  const toggleDefault = useCallback(
    () => setSaveAsDefault((reuse) => !reuse),
    [setSaveAsDefault],
  );

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setErrorMessage(undefined);
    if (!stripe || !elements) {
      toast.exception();
      setSubmitting(false);
      return;
    }
    if (!intentSecret) {
      return;
    }

    const { error, setupIntent } = await stripe.confirmCardSetup(intentSecret, {
      payment_method: {
        card: elements.getElement(CardElement) as any,
        metadata: {
          sessionId: sessionId || null,
          allowReuse: reuse ? 'true' : null,
        },
      },
      return_url: redirectTo,
    });
    setSubmitting(false);

    if (error && error.type === 'validation_error') {
      toast.invalidForm();
      return;
    }
    if (error) {
      toast.exception();
      setErrorMessage(error.message);
      return;
    }
    const paymentMethod =
      typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

    if (paymentMethod) {
      await addPaymentMethod({ paymentMethod, saveAsDefault });
      if (onSuccess) {
        onSuccess(paymentMethod);
      }
    }
  }, [
    stripe,
    elements,
    redirectTo,
    onSuccess,
    toast,
    addPaymentMethod,
    setSubmitting,
    setErrorMessage,
    intentSecret,
    reuse,
    sessionId,
    saveAsDefault,
  ]);

  return (
    <div>
      <PaymentMethodContainer selectable={selectableStyle}>
        <div className={classes.stripeContainer}>
          <CardElement />
          <ErrorMsg error={errorMessage} className={classes.errorMsg} />
        </div>
      </PaymentMethodContainer>
      <div className={classes.checkboxes}>
        <Checkbox
          noMargin
          label={t('billing.paymentMethod.add.reuse', {
            context: sessionId ? 'session' : 'payment',
          })}
          value={reuse}
          onChange={toggleReuse}
        />
        {!!reuse && (
          <Checkbox
            noMargin
            label={t('billing.paymentMethod.add.saveAsDefault')}
            value={saveAsDefault}
            onChange={toggleDefault}
          />
        )}
      </div>
      {!!elements && !!stripe && (
        <div className={classes.buttons}>
          {!!onCancel && (
            <Button
              variant="transparent"
              color="ink"
              small
              type="button"
              onClick={onCancel}
            >
              {t('billing.paymentMethod.add.cancel')}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSubmit}
            small
            isLoading={isSubmitting}
          >
            {t('billing.paymentMethod.add.submit')}
          </Button>
        </div>
      )}
    </div>
  );
};
